import { includes, isObject, mergeWith } from "lodash";
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { BillingFieldsPartial } from "rivals/components/shared/Form/Address/types";
import { BaseProps as BillingFormProps } from "rivals/components/shared/Form/BillingInformation/types";
import { getPayPalUniqueTransactionId } from "rivals/redux/user/selectors";
import {
  GiftPurchaseParams,
  RegistrationParams
} from "rivals/services/registration/types";
import { CARD_TYPE, PAYPAL_SUBSCRIPTION } from "rivals/shared/constants";
import {
  AddressFormKey,
  AddressFormState,
  AutoCompleteState,
  CardEvent,
  CardFormKey,
  Checkout,
  CheckoutField,
  CheckoutInput,
  DebitAgreedState,
  FORM_TEXT,
  FormAction,
  FormKey,
  FormState,
  ObickOptions
} from "./types";
import { BillingParams } from "../updateBillingInformation/types";

declare function obick(arg: { serverUrl: string }): Promise<Checkout>;

/**
 * Custom hook to be consumed by all components that require a billing form
 * Handles the use of the obick script, credit card field validation and returns
 * a token.
 */
function useObick({
  obickSourceUrl,
  initialState,
  createAccountAction,
  giftAction,
  updateBillingAction,
  payPalEmail,
  payPalToken,
  payPalAddress,
  formReady = true,
  enableOptIn = true,
  autoRenewalOptInStates
}: ObickOptions): {
  billingFormProps: BillingFormProps;
  debitAgreedState: DebitAgreedState;
  isCheckingOut: boolean;
  isDebitAgreed: boolean;
  submit: (submissionParams?: RegistrationParams | GiftPurchaseParams) => void;
  valid: boolean;
  validate: () => boolean;
} {
  function validateAddressInput(
    name: AddressFormKey,
    value: string,
    state: AddressFormState
  ): string {
    const { custom, required, regex } = state[name].validation;
    if (required && value === "") {
      return FORM_TEXT.REQUIRED_ERROR;
    }
    if (regex && !regex.test(value)) {
      return FORM_TEXT.INVALID_ERROR;
    }
    if (custom && !custom(value)) {
      return FORM_TEXT.INVALID_ERROR;
    }
    return "";
  }

  function reducer(state: FormState, action: FormAction): FormState {
    let error = "";
    switch (action.type) {
      case "card-error":
        return {
          ...state,
          [action.payload.name]: {
            error: action.payload.error
          }
        };
      case "clear-card-error":
        return {
          ...state,
          [action.payload.name]: {
            error: ""
          }
        };
      case "bulk-edit": {
        // Deep merges form state instead overwriting
        const customizer = (
          obj: Partial<AutoCompleteState>,
          src: Partial<AutoCompleteState>
        ): Partial<AutoCompleteState> => {
          return { ...obj, ...src };
        };
        const newState = mergeWith({ ...state }, action.payload, customizer);
        return newState;
      }
      case "edit":
        return {
          ...state,
          [action.payload.name]: {
            ...state[action.payload.name],
            value: action.payload.value
          }
        };
      case "validate":
        error = validateAddressInput(
          action.payload.name,
          action.payload.value,
          state
        );
        return {
          ...state,
          [action.payload.name]: {
            ...state[action.payload.name],
            error: error
          }
        };
      case "address-error":
        return {
          ...state,
          [action.payload.name]: {
            ...state[action.payload.name],
            error: action.payload.error
          }
        };
      case "reset-form":
        return initialState;
      default:
        return state;
    }
  }

  const [billingFormState, dispatch] = useReducer(reducer, initialState);
  const [isDebit, setIsDebit] = useState(false);
  const [debitDisabled, disableDebit] = useState(false);
  const [paymentType, setPaymentType] = useState<CARD_TYPE | undefined>();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [requiresAutoRenewalOptIn, setRequiresAutoRenewalOptIn] = useState(
    false
  );
  const [isAutoRenewAgreed, setIsAutoRenewAgreed] = useState(false);
  const debitAgreedState = useState(false);
  const [isDebitAgreed, setIsDebitAgreed] = debitAgreedState;
  const uniquePaypalTransId = useSelector(getPayPalUniqueTransactionId);

  // If payment type changes, hide/show the debit checkboxes approprately
  useEffect(() => {
    if (paymentType === CARD_TYPE.AMEX || paymentType === CARD_TYPE.DISCOVER) {
      setIsDebit(false);
      disableDebit(true);
    } else {
      disableDebit(false);
    }
  }, [paymentType]);

  // Reset the debit agreement when isDebit is changed
  useEffect(() => {
    if (!isDebit) {
      setIsDebitAgreed(false);
    }
  }, [isDebit]);

  // Depending on the state selected, show/hide annual opt in checkbox
  useEffect(() => {
    if (
      includes(
        autoRenewalOptInStates,
        payPalToken ? payPalAddress?.state : billingFormState.state.value
      ) &&
      enableOptIn
    ) {
      setIsAutoRenewAgreed(false);
      setRequiresAutoRenewalOptIn(true);
    } else {
      setIsAutoRenewAgreed(false);
      setRequiresAutoRenewalOptIn(false);
    }
  }, [billingFormState.state.value, payPalAddress?.state, enableOptIn]);

  const cardInput = useRef<Promise<CheckoutInput> | undefined>();
  const ccField = useRef<HTMLDivElement | null>(null);
  const cvvField = useRef<HTMLDivElement | null>(null);

  function startCheckout(): Promise<string> {
    setIsCheckingOut(true);
    if (payPalToken) {
      return Promise.resolve("");
    }
    if (!cardInput.current) {
      throw new Error("Failed to initialize card reader");
    }
    return cardInput.current.then(input => {
      return input.createToken().then(token => {
        return token;
      });
    });
  }

  function validateCardInput(name: CardFormKey): (e: CardEvent) => void {
    return function (e): void {
      if (e.dirty && !e.hasFocus && e.errors) {
        dispatch({
          payload: {
            error: e.errors.missing
              ? FORM_TEXT.REQUIRED_ERROR
              : FORM_TEXT.INVALID_ERROR,
            name: name
          },
          type: "card-error"
        });
      } else if (e.hasFocus) {
        dispatch({
          payload: {
            name: name
          },
          type: "clear-card-error"
        });
      }
      if (e.leadingDigits && paymentType !== e.paymentType) {
        setPaymentType(e.paymentType);
      }
    };
  }

  function obiFieldStyle(field: CheckoutField): { ["::disabled"]: boolean } {
    return {
      "::disabled": field.input.status().generatingToken // eslint-disable-line @typescript-eslint/naming-convention
    };
  }

  function initializeObi(): void {
    cardInput.current = obick({
      serverUrl: `${obickSourceUrl}/obipmservice`
    }).then(checkout => {
      return checkout
        .input({
          theme: {
            iframe: {
              cardNumber: {
                style: obiFieldStyle
              },
              cvv: {
                style: obiFieldStyle
              }
            }
          }
        })
        .then(input => {
          return input;
        });
    });

    cardInput.current.then(input => {
      input.cardNumber().onStatusUpdate(validateCardInput("cardNumber"));
      input.cvv().onStatusUpdate(validateCardInput("cvv"));
    });
  }

  useEffect(() => {
    if (formReady) {
      const script = window.document.createElement("script");

      script.src = `${obickSourceUrl}/obipmservice/obick.umd.js`;
      script.async = true;
      script.onload = initializeObi;

      window.document.body.appendChild(script);

      return (): void => {
        window.document.body.removeChild(script);
      };
    } else {
      dispatch({
        type: "reset-form"
      });
      setIsAutoRenewAgreed(false);
      setIsDebit(false);
    }
  }, [formReady]);

  function validate(): boolean {
    let valid = true;
    for (const k in billingFormState) {
      const addressKey = k as AddressFormKey;
      const cardKey = k as CardFormKey;
      if (billingFormState[addressKey].value !== undefined) {
        const error = validateAddressInput(
          addressKey,
          billingFormState[addressKey].value,
          billingFormState
        );
        if (error) {
          valid = false;
          dispatch({
            payload: { error: error, name: k as AddressFormKey },
            type: "address-error"
          });
        }
      } else {
        if (ccField.current && cardKey === "cardNumber") {
          if (ccField.current.classList.contains("obick-invalid")) {
            valid = false;
            dispatch({
              payload: {
                error: FORM_TEXT.REQUIRED_ERROR,
                name: "cardNumber"
              },
              type: "card-error"
            });
          }
        } else if (cvvField.current && cardKey === "cvv") {
          if (cvvField.current.classList.contains("obick-invalid")) {
            valid = false;
            dispatch({
              payload: {
                error: FORM_TEXT.REQUIRED_ERROR,
                name: "cvv"
              },
              type: "card-error"
            });
          }
        }
      }
    }
    return valid;
  }

  const handleAddressInputBlur = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch({
      payload: {
        name: e.target.name as AddressFormKey,
        value: e.target.value
      },
      type: "validate"
    });
  };

  const handleAddressInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch({
      payload: {
        name: e.target.name as AddressFormKey,
        value: e.target.value
      },
      type: "edit"
    });
  };

  const handleStateInputChange = (value: string): void => {
    dispatch({
      payload: {
        name: "state",
        value: value
      },
      type: "edit"
    });
  };

  const updateAddress = (autoCompleteResults: BillingFieldsPartial): void =>
    dispatch({
      payload: autoCompleteResults,
      type: "bulk-edit"
    });

  const valid = useMemo(() => {
    if (payPalToken && !isObject(payPalAddress)) {
      return false;
    }
    for (const k in billingFormState) {
      if (billingFormState[k as FormKey].error !== "") {
        return false;
      }
    }
    if (isDebit && !isDebitAgreed) {
      return false;
    }
    if (requiresAutoRenewalOptIn && !isAutoRenewAgreed) {
      return false;
    }
    return true;
  }, [
    billingFormState,
    isDebit,
    isDebitAgreed,
    requiresAutoRenewalOptIn,
    isAutoRenewAgreed,
    payPalToken,
    payPalAddress
  ]);

  const getPayPalParams = (): BillingParams => {
    const {
      city = "",
      street2 = "",
      street1 = "",
      firstName = "",
      lastName = "",
      zip = "",
      state = ""
    } = payPalAddress || {};
    return {
      autoRenewalOptIn: isAutoRenewAgreed,
      cardExp: "",
      cardType: PAYPAL_SUBSCRIPTION,
      city,
      firstName,
      lastName,
      obiToken: payPalToken || "",
      state,
      street1,
      street2,
      uniquePaypalTransId,
      zip
    };
  };

  function submit(
    submissionParams?: RegistrationParams | GiftPurchaseParams
  ): void {
    if (!payPalToken && (!validate() || !paymentType)) {
      return;
    }
    const [mm, yy] = billingFormState.expirationDate.value.split("/");
    const expiration = `${mm}/20${yy}`;
    startCheckout()
      .then(token => {
        const billingParams: BillingParams = payPalToken
          ? getPayPalParams()
          : {
              autoRenewalOptIn: isAutoRenewAgreed,
              cardExp: expiration,
              cardType: isDebit ? `${paymentType}_DEBIT` : paymentType || "",
              city: billingFormState.city.value,
              firstName: billingFormState.firstName.value,
              lastName: billingFormState.lastName.value,
              obiToken: token,
              state: billingFormState.state.value,
              street1: billingFormState.street1.value,
              street2: billingFormState.street2.value,
              zip: billingFormState.zip.value
            };
        if (createAccountAction && submissionParams) {
          const registrationParams = submissionParams as RegistrationParams;
          createAccountAction(registrationParams, billingParams);
        } else if (updateBillingAction) {
          updateBillingAction({ ...billingParams, paypalEmail: payPalEmail });
        } else if (giftAction && submissionParams) {
          const giftingParams = submissionParams as GiftPurchaseParams;
          giftAction(giftingParams, billingParams);
        }
        setIsCheckingOut(false);
      })
      .catch(() => setIsCheckingOut(false));
  }

  return {
    billingFormProps: {
      billingFormState,
      ccField,
      cvvField,
      debitDisabled,
      isAutoRenewAgreed,
      isDebit,
      onBlur: handleAddressInputBlur,
      onChange: handleAddressInputChange,
      onStateChange: handleStateInputChange,
      requiresAutoRenewalOptIn,
      setIsAutoRenewAgreed,
      setIsDebit,
      updateAddress
    },
    debitAgreedState,
    isCheckingOut,
    isDebitAgreed,
    submit,
    valid,
    validate
  };
}

export default useObick;
